import { monedaLocalDePais } from "../brutoCapex";
import { getDolarValue, getUFValue } from "../exchangeRateService";
import { client } from "../supabase";
import { getSaldosAmortizacionPorCompromiso } from "./amortizacionCompromiso";

// ─── Fase 2a/2b (proyección/factura/pago, 2026-10-05) — vínculo factura↔compromiso ────────────
//
// REDISEÑADO (Cowork, decisión de Ignacio 2026-10-05 10:28) — reemplaza el diseño original de
// este archivo: la factura NO cierra el compromiso entero, baja su REMANENTE; un compromiso puede
// recibir VARIAS facturas (y además seguir amortizándose por el checkbox — ambas vías restan del
// mismo remanente, YA NO son mutuamente excluyentes por `estado`, ver migración). Nunca se pisa
// `compromisos_pago.monto` — la cobertura vive en `asociaciones_compromiso_factura.montos_
// aplicados` (molde línea por línea: `src/utils/activos.js`, Fase 1a).
//
// Migración: supabase/migrations/20261005c_proyeccion_factura_pago_compromisos_rediseno.sql
// (ESCRITA SIN APLICAR — 10.137).
//
// Diseño: docs/decisiones/2026-10-05-proyeccion-factura-pago-fase2-diseno.md §3.3 (adenda de
// corrección, 2026-10-05 10:28).

// Facturas candidatas para vincular a UN compromiso: del mismo proveedor/país, SII recibida
// (nunca externa — mismo criterio que activos), excluyendo NC (tipo_dte=61). A diferencia de la
// versión anterior, NO excluye facturas que ya tengan una asociación activa con OTROS
// compromisos — una factura puede, en teoría, cubrir más de un compromiso a la vez (mismo
// p_aplicaciones de la RPC); la RPC rechaza re-vincular la MISMA factura dos veces (índice único
// parcial), eso sí lo sigue prefiltrando el picker.
export const getFacturasParaVincularCompromiso = async ({ pais, proveedorId }) => {
  if (!pais || !proveedorId) return [];
  const { data: activas, error: eActivas } = await client
    .from("asociaciones_compromiso_factura")
    .select("factura_sii_recibida_id")
    .eq("estado", "activa");
  if (eActivas) throw eActivas;
  const idsConAsociacion = (activas || []).map((a) => a.factura_sii_recibida_id);

  let query = client
    .from("facturas_sii_recibidas")
    .select("id, folio, fecha_emision, monto_neto")
    .eq("pais", pais)
    .eq("proveedor_id", proveedorId)
    .neq("tipo_dte", 61)
    .order("fecha_emision", { ascending: false });
  if (idsConAsociacion.length > 0) query = query.not("id", "in", `(${idsConAsociacion.join(",")})`);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((f) => ({
    id: f.id,
    label: `Folio ${f.folio}`,
    fechaEmision: f.fecha_emision,
    montoNeto: Number(f.monto_neto),
  }));
};

// Saldo FACTURADO vigente por compromiso (molde `getSaldosAmortizacionPorCompromiso` de
// `amortizacionCompromiso.js`) — Σ `montos_aplicados[compromiso_id]` de las asociaciones
// `activa`, en la moneda del COMPROMISO (ya convertida, nunca se reconvierte acá). UNA sola
// query por país (10.234). Devuelve Map<compromiso_id, { facturado }>.
export const getSaldosFacturacionPorCompromiso = async (pais) => {
  const map = new Map();
  if (!pais) return map;
  const { data, error } = await client
    .from("asociaciones_compromiso_factura")
    .select("compromiso_ids, montos_aplicados")
    .eq("pais", pais)
    .eq("estado", "activa");
  if (error) throw error;
  for (const row of data || []) {
    for (const compromisoId of row.compromiso_ids || []) {
      const monto = Number(row.montos_aplicados?.[String(compromisoId)] ?? 0);
      const acc = map.get(compromisoId) || { facturado: 0 };
      acc.facturado += monto;
      map.set(compromisoId, acc);
    }
  }
  return map;
};

// Fuente ÚNICA del remanente de un compromiso (10.229 — un hecho se mide con el MISMO predicado
// en todos sus lectores; molde de la regla que ya aplican `vincular_factura_compromiso`/
// `amortizar_compromiso`/el trigger de sobreamortización, 20261005c/20261005d). UNA sola query de
// `compromisos_pago` del país + los 2 batches de saldo (amortizado/facturado), YA combinados.
// Devuelve Map<compromiso_id, { monto, moneda, amortizado, facturado, remanente }> — o `null` si
// CUALQUIERA de los 2 batches de saldo falló (10.177: un fetch caído nunca se disfraza de "sin
// amortizar/facturar", que inflaría el remanente mostrado). La query de `compromisos_pago` en sí
// SÍ propaga el error (molde `getCompromisos`, nunca se traga un error de lectura — 10.170).
export const getRemanentePorCompromiso = async (pais) => {
  if (!pais) return new Map();
  const { data: compromisos, error } = await client
    .from("compromisos_pago")
    .select("id, monto, moneda")
    .eq("pais", pais)
    .eq("eliminado", false);
  if (error) throw error;

  const [amortizadoMap, facturadoMap] = await Promise.all([
    getSaldosAmortizacionPorCompromiso(pais).catch(() => null),
    getSaldosFacturacionPorCompromiso(pais).catch(() => null),
  ]);
  if (amortizadoMap === null || facturadoMap === null) return null;

  const map = new Map();
  for (const c of compromisos || []) {
    const amortizado = amortizadoMap.get(c.id)?.amortizado || 0;
    const facturado = facturadoMap.get(c.id)?.facturado || 0;
    map.set(c.id, {
      monto: Number(c.monto) || 0,
      moneda: c.moneda,
      amortizado,
      facturado,
      remanente: (Number(c.monto) || 0) - amortizado - facturado,
    });
  }
  return map;
};

// Compromisos candidatos para vincular a UNA factura ya conocida: planificados, no eliminados,
// del mismo país, y del mismo proveedor O todavía sin proveedor asignado (decisión e: un
// compromiso SIN proveedor —presupuesto— acepta facturas de cualquier proveedor; CON proveedor,
// solo del mismo — mismo criterio que `getActivosParaVincularFactura`). A diferencia de la
// versión anterior, NO filtra por `factura_sii_recibida_id IS NULL` (ese FK queda siempre NULL
// bajo el modelo nuevo — un compromiso puede recibir varias facturas): filtra por REMANENTE > 0
// (`getRemanentePorCompromiso`, fuente única), que es el candado real que la RPC aplica.
export const getCompromisosParaVincularFactura = async ({ pais, proveedorId }) => {
  if (!pais) return [];
  const { data, error } = await client
    .from("compromisos_pago")
    .select("id, concepto, monto, moneda, fecha_estimada, proveedor_id")
    .eq("pais", pais)
    .eq("estado", "planificado")
    .eq("eliminado", false)
    .or(proveedorId ? `proveedor_id.is.null,proveedor_id.eq.${proveedorId}` : "proveedor_id.is.null")
    .order("fecha_estimada", { ascending: true });
  if (error) throw error;
  const compromisos = data || [];
  if (compromisos.length === 0) return [];

  const remanentes = await getRemanentePorCompromiso(pais);
  // Sin datos confiables de saldo (10.177) — no ofrecer candidatos con un remanente que podría
  // estar mal, en vez de arriesgar un sobre-aplicar silencioso.
  if (remanentes === null) return [];

  return compromisos
    .map((c) => ({ ...c, remanente: remanentes.get(c.id)?.remanente ?? Number(c.monto) }))
    .filter((c) => c.remanente > 0.5);
};

// Conversión de moneda para el monto a aplicar (decisión c, 2026-10-05): si el compromiso está en
// la misma moneda local del país de la factura, SIN conversión (tc=null). Si el compromiso está
// en USD o UF, se convierte con el TC/valor UF de la FECHA DE EMISIÓN de la factura (criterio
// único del proyecto, NIC 21.21 —
// docs/decisiones/2026-10-04-reingenieria-ebitda-decision-criterio-tc.md) y se devuelve el TC
// usado para guardarlo en la asociación (auditoría). `facturas_sii_recibidas` NO tiene columna
// `moneda` — el monto de la factura SIEMPRE está en la moneda local del país (CLP/PEN), 10.28.
export const calcularMontoAplicadoCompromiso = (montoFacturaLocal, fechaEmisionFactura, compromisoMoneda, pais) => {
  const monedaLocal = monedaLocalDePais(pais);
  const montoLocal = Number(montoFacturaLocal) || 0;
  if (compromisoMoneda === monedaLocal) return { monto: montoLocal, tc: null };
  if (compromisoMoneda === "USD") {
    const tc = getDolarValue(fechaEmisionFactura, pais);
    return { monto: tc ? montoLocal / tc : 0, tc };
  }
  if (compromisoMoneda === "UF") {
    const tc = getUFValue(fechaEmisionFactura);
    return { monto: tc ? montoLocal / tc : 0, tc };
  }
  // Caso no soportado hoy (ej. compromiso PEN con factura CLP, cruce de país) — sin TC confiable
  // para convertir con rigor (mismo límite que 8.2 de amortizaciones_compromiso).
  return { monto: montoLocal, tc: null };
};

// RPC `vincular_factura_compromiso` (único alta). `aplicaciones`: { [compromisoId]: { monto, tc } }
// — monto en la moneda del COMPROMISO (ya convertido por `calcularMontoAplicadoCompromiso` si
// corresponde), tc el usado (null si no hubo conversión).
export const vincularFacturaCompromiso = async ({ facturaId, aplicaciones, usuarioId = null }) => {
  const { data, error } = await client.rpc("vincular_factura_compromiso", {
    p_factura_id: facturaId,
    p_aplicaciones: aplicaciones,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};

// RPC `desvincular_factura_compromiso` (reversa por evento — nunca borra la fila; NO restaura
// nada en compromisos_pago, decisión b, nunca se tocó).
export const desvincularFacturaCompromiso = async ({ asociacionId, usuarioId = null }) => {
  const { data, error } = await client.rpc("desvincular_factura_compromiso", {
    p_asociacion_id: asociacionId,
    p_usuario_id: usuarioId,
  });
  if (error) throw error;
  return data;
};
